import { useCallback, useEffect, useMemo } from 'react'
import toast from 'react-hot-toast'
import { useLocation, useSearchParams } from 'react-router'

import {
  parseQueryString,
  type Query,
  setDataByPage,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@fv/client-core'
import {
  type AuditCountsDTO,
  type AuditSortType,
  type AuditStatus,
  parseSortDir,
  type SortDir,
} from '@fv/models'
import { removeNilOrEmpty } from '@fv/models/core'

import { defaultPageSize } from '@/constants'
import { useRemoveFromList } from '@/hooks/shipments'
import { shipperFetch } from '@/utils/shipperFetch'
import { normalizeQueryString } from '@/utils/stringTransforms'

import { useSortDirectionPreference } from '../shipment-list/hooks/useSortDirectionPreference'
import type { Audit } from './types'

// status is in the query key separately, so exclude it from the normalized string
const normalizeAuditQs = (qs: string) => normalizeQueryString(qs, ['status'])
const normalizeAuditCountsQs = (qs: string) =>
  normalizeQueryString(normalizeAuditQs(qs), ['sortBy', 'sortDirection'])

export const auditKeys = {
  all: ['audits'] as const,
  counts: () => [...auditKeys.all, 'counts'] as const,
  list: (status: AuditStatus | null) => [...auditKeys.all, status] as const,
  filteredCounts: (qs: string) => {
    return [...auditKeys.counts(), normalizeAuditCountsQs(qs)] as const
  },
  filteredList: (status: AuditStatus | null, qs: string) => {
    return [...auditKeys.list(status), normalizeAuditQs(qs)] as const
  },
}

export type AuditListCounts = AuditCountsDTO

export const defaultAuditStatus = 'outstanding'
export const AUDIT_LOCAL_STORAGE_SORTDIR_KEY = 'audit-list-sort-direction'
const defaultAuditSortDirection: SortDir = 1

export function useAuditListStatus() {
  const [searchParams, setSearchParams] = useSearchParams()
  const status = searchParams.get('status[]') as AuditStatus | null

  useEffect(() => {
    if (!status) {
      setSearchParams({
        ...parseQueryString(searchParams.toString()),
        'status[]': [defaultAuditStatus],
      })
    }
  }, [searchParams, setSearchParams, status])

  return status
}

export function useAuditListSort(status: AuditStatus | null) {
  const [searchParams] = useSearchParams()
  const [preferredSortDirection] = useSortDirectionPreference(
    AUDIT_LOCAL_STORAGE_SORTDIR_KEY,
  )
  const sortBy: AuditSortType =
    status === 'accepted' && searchParams.get('sortBy') === 'acceptedDate'
      ? 'acceptedDate'
      : 'pickupDate'

  return {
    sortBy,
    sortDirection:
      parseSortDir(
        searchParams.get('sortDirection') ?? preferredSortDirection,
      ) ?? defaultAuditSortDirection,
  }
}

const countedStatuses = ['outstanding', 'accepted', 'disputed'] as const

function calculateBucketChanges(
  prev: Pick<Audit, 'statuses'>,
  current: Pick<Audit, 'statuses'>,
): AuditListCounts {
  const inBucket = (audit: Pick<Audit, 'statuses'>, status: AuditStatus) =>
    audit.statuses.includes(status) ? 1 : 0

  const changes: AuditListCounts = {
    outstanding: 0,
    accepted: 0,
    disputed: 0,
    archived: 0,
  }

  countedStatuses.forEach(status => {
    changes[status] = inBucket(current, status) - inBucket(prev, status)
  })

  return changes
}

export type PagedAudits = {
  continuationToken?: string
  data: Audit[]
  hasMore?: boolean
}

export type InfiniteAuditQueryData = {
  pageParams: Array<number | null>
  pages: PagedAudits[]
}

export function buildCountsQueryFilter(qs: string) {
  const currentQuery = normalizeAuditCountsQs(qs)

  return {
    queryKey: auditKeys.counts(),
    predicate: (q: Query) => {
      const queryKeyParams = q.queryKey[2]
      return queryKeyParams === '' || queryKeyParams === currentQuery
    },
  }
}

function applyBucketChanges(
  counts: AuditListCounts | undefined,
  changes: AuditListCounts,
) {
  if (!counts) {
    return
  }

  return {
    accepted: Math.max(counts.accepted + changes.accepted, 0),
    archived: Math.max(counts.archived + changes.archived, 0),
    disputed: Math.max(counts.disputed + changes.disputed, 0),
    outstanding: Math.max(counts.outstanding + changes.outstanding, 0),
  }
}

export function updateAuditList(audits: Audit[], audit: Audit) {
  return audits.map(a => (a.loadId === audit.loadId ? audit : a))
}

export function useApplyStatusChange(currentView: AuditStatus) {
  const { search } = useLocation()
  const queryClient = useQueryClient()
  const removeFromList = useRemoveFromList()

  return useCallback(
    ({ previous, current }: { previous: Audit; current: Audit }) => {
      const isInCurrentList = current.statuses.includes(currentView)

      if (!isInCurrentList) {
        removeFromList({
          loadId: current.loadId,
          queryFilter: [...auditKeys.list(currentView)],
        })
      } else {
        queryClient.setQueriesData<InfiniteAuditQueryData>(
          { queryKey: auditKeys.list(currentView) },
          setDataByPage(audits => updateAuditList(audits, current)),
        )
      }

      const bucketChanges = calculateBucketChanges(previous, current)
      const changedBuckets = Object.keys(bucketChanges).filter(
        b => bucketChanges[b as AuditStatus] !== 0 && b !== currentView,
      ) as AuditStatus[]

      changedBuckets.forEach(status =>
        queryClient.invalidateQueries({ queryKey: auditKeys.list(status) }),
      )

      queryClient.setQueriesData<AuditListCounts | undefined>(
        buildCountsQueryFilter(search),
        prev => applyBucketChanges(prev, bucketChanges),
      )

      // force a refetch from the server after a delay to ensure the counts are fresh
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: auditKeys.counts() })
      }, 5000)
    },
    [queryClient, removeFromList, currentView, search],
  )
}

async function fetchAuditList(qs: string): Promise<PagedAudits> {
  return shipperFetch<PagedAudits>(`/audit?${qs}`)
}

export function useAuditList() {
  const { search } = useLocation()
  const status = useAuditListStatus()
  const { sortBy, sortDirection } = useAuditListSort(status)
  const listParams = new URLSearchParams(search)
  listParams.set('sortBy', sortBy)
  listParams.set('sortDirection', sortDirection.toString())
  const listQs = listParams.toString()

  const auditsQuery = useInfiniteQuery({
    queryKey: auditKeys.filteredList(status, listQs),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams(listQs)
      params.set('limit', String(defaultPageSize))
      params.set('continuationToken', pageParam)

      return fetchAuditList(params.toString())
    },
    initialPageParam: '',
    enabled: Boolean(status),
    getNextPageParam: lastPage => {
      if (!lastPage?.hasMore) return
      return lastPage.continuationToken
    },
  })

  const auditList = useMemo(
    () =>
      (auditsQuery.data?.pages ?? []).reduce(
        (all, { data }) => all.concat(...data),
        [] as Audit[],
      ),
    [auditsQuery.data?.pages],
  )

  // Handle invalid filters
  useEffect(() => {
    if (
      auditsQuery.error instanceof Error &&
      auditsQuery.error.name === 'EventValidationError'
    ) {
      toast.error(auditsQuery.error.message)
    }
  }, [auditsQuery.error])

  return {
    ...auditsQuery,
    data: auditList,
  }
}

async function fetchAuditListCounts(qs: string): Promise<AuditListCounts> {
  return shipperFetch<AuditListCounts>(`/audit/counts${qs}`)
}

export function useAuditListCount(status: AuditStatus) {
  const { search } = useLocation()
  const listCountsQuery = useQuery(auditKeys.filteredCounts(search), () => {
    return fetchAuditListCounts(search)
  })

  const count = listCountsQuery.data?.[status]
  const hasFilters =
    Object.keys(
      removeNilOrEmpty(parseQueryString(normalizeAuditCountsQs(search))),
    ).length > 0

  return useMemo(
    () => ({
      count: count ?? 0,
      hasFilters,
      isLoading: listCountsQuery.isLoading,
    }),
    [count, hasFilters, listCountsQuery.isLoading],
  )
}

type AuditSettings = {
  autoArchiveShipment?: boolean
  emailDispute?: boolean
  carriers: Array<{
    carrierId: string
    emails: string[]
  }>
}

async function fetchAuditSettings(): Promise<AuditSettings> {
  return shipperFetch<AuditSettings>('/audit/settings')
}

function useAuditSettings() {
  return useQuery(['audit-settings'], fetchAuditSettings, {
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  })
}

export function useAuditSetting(
  setting: 'autoArchiveShipment' | 'emailDispute',
) {
  const settingsQuery = useAuditSettings()
  const auditSettings: AuditSettings | undefined = settingsQuery.data

  return useMemo(() => {
    return auditSettings?.[setting] ?? false
  }, [auditSettings, setting])
}

export function useCarrierEmails(carrierId: string) {
  const settingsQuery = useAuditSettings()
  const auditSettings: AuditSettings | undefined = settingsQuery.data

  return useMemo(() => {
    return (
      auditSettings?.carriers?.find(c => c.carrierId === carrierId)?.emails ??
      []
    )
  }, [auditSettings, carrierId])
}
